import type { ColumnDef } from '@tanstack/react-table';

import {
    type GetCatalogProductsQuery,
    type CatalogProductsFilterInput,
    ProductState,
    InfoType,
} from 'data/__generated__';
import { textColumn, multiSelectColumn } from 'components/DataTable';

import { formatPrice } from 'utils/price';

import { StatusTag } from 'pages/Products/components/StatusTag';
import { InfoTag } from 'pages/Products/components/InfoTag';
import { TotemImage } from 'components/TotemImage';
import { PRODUCTS_STATES_LABELS } from './states';

export type ProductRow = GetCatalogProductsQuery['catalogProducts']['products'][number];

function getInfoTypeLabel(infoType: InfoType): string {
    switch (infoType) {
        case InfoType.Private:
            return 'Privé';
        case InfoType.IsToBeArchived:
            return 'À sortir';
        case InfoType.New:
            return 'Nouveau';
        case InfoType.IsInShortage:
            return 'Rupture fournisseur';
        case InfoType.IsBeingTested:
            return 'En test';
        case InfoType.Paused:
            return 'En pause';
        case InfoType.OutOfStock:
            return 'En rupture';
        default:
            return '';
    }
}

function getProductInfoTypes(product: ProductRow): InfoType[] {
    const infoTags: InfoType[] = [];

    if (product.access.length) {
        infoTags.push(InfoType.Private);
    }
    if (product.supplierShortageInfo.inShortage) {
        infoTags.push(InfoType.IsInShortage);
    }
    if (product.isToBeArchived) {
        infoTags.push(InfoType.IsToBeArchived);
    }
    if (product.isBeingTested) {
        infoTags.push(InfoType.IsBeingTested);
    }
    if (product.newUntil) {
        infoTags.push(InfoType.New);
    }
    if (product.isPaused) {
        infoTags.push(InfoType.Paused);
    }
    if (product.isOutOfStock) {
        infoTags.push(InfoType.OutOfStock);
    }

    return infoTags;
}

export const productsColumns: ColumnDef<ProductRow, unknown>[] = [
    {
        id: 'image',
        accessorKey: 'imageUrl',
        header: 'Photo',
        enableSorting: false,
        meta: { defaultVisible: true },
        cell: ({ row }) => <TotemImage src={row.original.imageUrl || ''} alt={row.original.name} size={50} />,
    } as ColumnDef<ProductRow, unknown>,
    textColumn<ProductRow, CatalogProductsFilterInput>({
        id: 'fullname',
        accessorFn: (row) => {
            let fullName = row.name;
            if (row.brand) fullName += ` - ${row.brand}`;
            if (row.volume) fullName += ` - ${row.volume}`;
            return fullName;
        },
        header: 'Nom',
        defaultVisible: true,
    }),
    multiSelectColumn<ProductRow, CatalogProductsFilterInput>({
        accessorKey: 'state',
        header: 'Statut',
        filterKey: 'states',
        defaultVisible: true,
        options: Object.values(ProductState).map((state) => ({
            value: state,
            label: PRODUCTS_STATES_LABELS[state] || state,
        })),
        renderOption: (option) => <StatusTag state={option.value as ProductState} />,
        cell: ({ row }) => <StatusTag state={row.original.state} />,
    }),
    multiSelectColumn<ProductRow, CatalogProductsFilterInput>({
        id: 'infos',
        accessorFn: (row) => getProductInfoTypes(row).join(', '),
        header: 'Info',
        filterKey: 'infos',
        defaultVisible: true,
        options: Object.values(InfoType).map((infoType) => ({
            value: infoType,
            label: getInfoTypeLabel(infoType),
        })),
        renderOption: (option) => <InfoTag infoTag={option.value as InfoType} />,
        cell: ({ row }) => {
            const infoTypes = getProductInfoTypes(row.original);
            return (
                <div className="flex flex-wrap gap-1">
                    {infoTypes.map((infoTag) => (
                        <InfoTag key={infoTag} infoTag={infoTag} />
                    ))}
                </div>
            );
        },
    }),
    textColumn<ProductRow, CatalogProductsFilterInput>({
        id: 'categoryTitle',
        accessorFn: (row) => row.category.title,
        header: 'Catégorie',
        filterKey: 'categories',
        defaultVisible: true,
        cell: ({ row }) => {
            const { category, subcategory } = row.original;
            return (
                <div>
                    {category.title}
                    {subcategory ? (
                        <div className="flex flex-row">
                            {'\n'} ↳ <div>{subcategory.name}</div>
                        </div>
                    ) : null}
                </div>
            );
        },
    }),
    textColumn<ProductRow, CatalogProductsFilterInput>({
        id: 'tags',
        accessorFn: (row) => row.tags.map((tag) => tag.name).join(', '),
        header: 'Tag(s)',
        filterKey: 'tags',
        defaultVisible: true,
    }),
    textColumn<ProductRow, CatalogProductsFilterInput>({
        id: 'supplierName',
        accessorFn: (row) => row.suppliers[0]?.name || '',
        header: 'Fournisseur #1',
        filterKey: 'suppliers',
        defaultVisible: true,
    }),
    textColumn<ProductRow, CatalogProductsFilterInput>({
        id: 'supplierPrice',
        accessorFn: (row) => {
            const { prices = [] } = row.suppliers[0] || {};
            const { value: supplierPrice } = prices[0] || {};
            return supplierPrice !== undefined ? formatPrice(supplierPrice) : '';
        },
        header: "Prix d'achat",
        defaultVisible: true,
    }),
];
